import React, { useState, useRef, useEffect } from 'react';
import algoliasearch from 'algoliasearch/lite';
import {
  InstantSearch,
  Configure,
  connectHits,
  connectStats,
} from 'react-instantsearch-dom';
import QueryForm from './QueryForm';
import FeaturedList from './FeaturedList';
import UpcomingList from './UpcomingList';
import ListPager from './ListPager';
import Loader from './Loader';
import {
  ALGOLIA_APP_ID,
  ALGOLIA_SEARCH_KEY,
  ALGOLIA_INDEX_PREFIX,
} from '../../config/index.js';

console.log('[EventsList] Init', {
  algoliaAppId: ALGOLIA_APP_ID,
  algoliaPrefix: ALGOLIA_INDEX_PREFIX,
});

const searchClient =
  ALGOLIA_APP_ID && ALGOLIA_SEARCH_KEY
    ? algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY)
    : null;

if (!searchClient) {
  console.warn('[EventsList] Algolia searchClient not created — missing APP_ID or SEARCH_KEY');
}

// ─── Date helpers ─────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const formatAbbrDate = (dateObj) => {
  if (!dateObj) return '';
  return `${MONTH_ABBR[dateObj.getUTCMonth()]} ${dateObj.getUTCDate()}`;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const decodeEntities = (str) => {
  if (!str) return '';
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
};

// ─── Hit mapper ───────────────────────────────────────────────────────────────

const mapHit = (hit) => {
  const startMs = hit.start_date ? hit.start_date * 1000 : null;
  const endMs = hit.end_date ? hit.end_date * 1000 : null;
  const startDateObj = startMs ? new Date(startMs) : null;
  const endDateObj = endMs ? new Date(endMs) : null;
  const abbrStart = formatAbbrDate(startDateObj);
  const abbrEnd = formatAbbrDate(endDateObj);

  return {
    id: hit.objectID,
    url: hit.permalink,
    event_name: decodeEntities(hit.post_title),
    citytown: hit.citytown || '',
    phone_number: hit.phone_number || '',
    website: hit.website || '',
    event_description: hit.post_excerpt || '',
    the_content: '',
    // FeaturedEventItem uses start_date / end_date as formatted strings
    start_date: abbrStart,
    end_date: abbrEnd,
    // UpcomingItem uses these
    start_date_abbr: abbrStart,
    end_date_abbr: abbrEnd,
    event_month: startDateObj ? MONTH_NAMES[startDateObj.getUTCMonth()] : '',
    event_year: startDateObj ? startDateObj.getUTCFullYear() : new Date().getUTCFullYear(),
    start_timestamp: startMs || 0,
    active_featured_event: hit.active_featured_event,
  };
};

// ─── Connected component ──────────────────────────────────────────────────────
//
// Single connectHits renders both FeaturedList and UpcomingList.
// Because customRanking has desc(active_featured_event), featured events
// always sort to the top of page 0 — so the featured section naturally
// only has content on the first page (same as the original AJAX behavior).

const EventHits = connectHits(({ hits, onFirstLoad }) => {
  const loaded = useRef(false);

  React.useEffect(() => {
    if (!loaded.current) {
      loaded.current = true;
      console.log('[EventsList] First load complete — hiding loader');
      onFirstLoad();
    }
  }, [hits]);

  console.log(`[EventsList] Hits received: ${hits.length}`);

  // Log every unique event_type value seen in hits so we can compare
  // to what the dropdown filter sends (e.g. "Music/Theater" vs "Music/Theatre")
  const uniqueEventTypes = [...new Set(hits.map((h) => h.event_type).filter(Boolean))].sort();
  console.log('[EventsList] Unique event_type values in current hits:', uniqueEventTypes);

  const events = hits.map(mapHit).sort((a, b) => a.start_timestamp - b.start_timestamp);
  const featured = events.filter((e) => e.active_featured_event);
  const upcoming = events.filter((e) => !e.active_featured_event);

  console.log(`[EventsList] Featured: ${featured.length}, Upcoming: ${upcoming.length}`);

  if (events.length === 0) {
    return <NoResults />;
  }

  return (
    <>
      <FeaturedList featuredEvents={featured} isLoading={false} />
      <div
        id="gpt-ad-slot-square-2"
        className="ad__container ad__container--square u--margin-bottom-10"
      />
      <UpcomingList upcomingEvent={upcoming} />
    </>
  );
});

const StatsWrapper = connectStats(({ nbHits, children }) => children({ nbHits }));

// ─── Main component ───────────────────────────────────────────────────────────

export default function EventsList() {
  const [isLoading, setLoading] = useState(true);
  const [keywords, setKeywords] = useState('');
  const [region, setRegion] = useState('');
  const [eventType, setEventType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [limit, setLimit] = useState(24);
  const [page, setPage] = useState(0);
  const [facetOptions, setFacetOptions] = useState({ regions: [], eventTypes: [] });

  useEffect(() => {
    if (!searchClient) return;
    const indexName = `${ALGOLIA_INDEX_PREFIX}events`;
    const now = new Date();
    const todayTs = Math.floor(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()) / 1000);
    searchClient.search([{
      indexName,
      params: {
        query: '',
        facets: ['region', 'event_type'],
        numericFilters: [`start_date >= ${todayTs}`],
        hitsPerPage: 0,
      },
    }]).then(({ results }) => {
      const facets = results[0].facets || {};
      // Only include values with at least 1 hit (Algolia omits 0-count values
      // by default, but this guard ensures nothing slips through)
      const sortWithOtherLast = (a, b) => {
        if (a === 'Other') return 1;
        if (b === 'Other') return -1;
        return a.localeCompare(b);
      };
      const regions = Object.entries(facets.region || {})
        .filter(([, count]) => count > 0)
        .map(([val]) => val)
        .sort(sortWithOtherLast);
      const eventTypes = Object.entries(facets.event_type || {})
        .filter(([, count]) => count > 0)
        .map(([val]) => val)
        .sort(sortWithOtherLast);
      console.log('[EventsList] Facet options fetched from Algolia:', { regions, eventTypes });
      setFacetOptions({ regions, eventTypes });
    }).catch((err) => {
      console.warn('[EventsList] Failed to fetch facet options:', err);
    });
  }, []);

  if (!searchClient) return null;

  const handleQueryUpdate = ({ keywords: kw, region: r, type: t, startDate: sd, endDate: ed }) => {
    console.log('[EventsList] Query updated:', { kw, r, t, sd, ed });
    setKeywords(kw || '');
    setRegion(r || '');
    setEventType(t || '');
    setStartDate(sd || '');
    setEndDate(ed || '');
    setPage(0);
  };

  const buildFilters = () => {
    const parts = [];
    if (region) parts.push(`region:"${region}"`);
    if (eventType) {
      const filterStr = `event_type:"${eventType}"`;
      console.log('[EventsList] event_type filter string (exact):', filterStr,
        '| charCodes:', [...eventType].map((c) => c.charCodeAt(0)));
      parts.push(filterStr);
    }
    return parts.join(' AND ');
  };

  const buildNumericFilters = () => {
    const now = new Date();

    // Local midnight (browser timezone — may differ from Algolia Console which uses UTC)
    const localMidnightMs = new Date(now).setHours(0, 0, 0, 0);
    const localMidnightTs = Math.floor(localMidnightMs / 1000);

    // UTC midnight — matches how Algolia Console timestamps events
    const utcMidnightMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
    const utcMidnightTs = Math.floor(utcMidnightMs / 1000);

    console.log('[EventsList] Date boundary debug:', {
      browserTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      localMidnight: `${localMidnightTs} (${new Date(localMidnightMs).toISOString()})`,
      utcMidnight:   `${utcMidnightTs} (${new Date(utcMidnightMs).toISOString()})`,
      differenceSeconds: localMidnightTs - utcMidnightTs,
      // If difference > 0, local midnight is LATER than UTC — events between UTC midnight
      // and local midnight will show in Algolia Console but NOT on this page
    });

    const filters = [];

    if (startDate) {
      const ts = Math.floor(new Date(startDate).getTime() / 1000);
      console.log('[EventsList] Using user startDate filter:', startDate, '→', ts);
      filters.push(`start_date >= ${ts}`);
    } else {
      // Using UTC midnight so results match what Algolia Console reports
      console.log('[EventsList] Using UTC midnight as default start_date floor:', utcMidnightTs);
      filters.push(`start_date >= ${utcMidnightTs}`);
    }

    if (endDate) {
      const endMs = new Date(endDate);
      endMs.setHours(23, 59, 59);
      const ts = Math.floor(endMs.getTime() / 1000);
      console.log('[EventsList] Using user endDate filter:', endDate, '→', ts);
      filters.push(`start_date <= ${ts}`);
    }

    return filters;
  };

  const filters = buildFilters();
  const numericFilters = buildNumericFilters();

  console.log('[EventsList] Configure props being sent to Algolia:', {
    query: keywords || '(empty)',
    filters: filters || '(none)',
    numericFilters,
    hitsPerPage: limit,
    page,
    // Paste these into the Algolia Console to reproduce the exact query
  });

  const handleSetLimit = (newLimit) => {
    console.log(`[EventsList] Limit changed: ${limit} → ${newLimit}, resetting to page 0`);
    setLimit(newLimit);
    setPage(0);
  };

  const handleSetPage = (idx) => {
    console.log(`[EventsList] Page changed: ${page} → ${idx}`);
    setPage(idx);
  };

  return (
    <div id="scroll-anchor">
      <QueryForm
        updateQuery={handleQueryUpdate}
        query={{ keywords, region, type: eventType, startDate, endDate }}
        regions={facetOptions.regions}
        eventTypes={facetOptions.eventTypes}
      />

      <InstantSearch
        searchClient={searchClient}
        indexName={`${ALGOLIA_INDEX_PREFIX}events`}
      >
        <Configure
          hitsPerPage={limit}
          page={page}
          query={keywords}
          filters={filters}
          numericFilters={numericFilters}
        />

        {isLoading && <Loader />}
        <div
          style={isLoading ? { visibility: 'hidden', height: 0, overflow: 'hidden' } : {}}
        >
          <EventHits onFirstLoad={() => {
            setLoading(false);
            setTimeout(() => window.initializeAds && window.initializeAds(), 3000);
          }} />
          <StatsWrapper>
            {({ nbHits }) => {
              const totalPages = Math.ceil(nbHits / limit);
              console.log(
                `[EventsList] Stats: ${nbHits} total hits, ${totalPages} pages, showing page ${page + 1}`
              );
              return (
                <ListPager
                  changeLimit={handleSetLimit}
                  limit={limit}
                  setPagerIndex={handleSetPage}
                  nextPage={() => handleSetPage(Math.min(page + 1, totalPages - 1))}
                  prevPage={() => handleSetPage(Math.max(page - 1, 0))}
                  pagerIndex={page}
                  totalEvents={nbHits}
                  totalPages={totalPages}
                  paginatedItemCount={Math.min((page + 1) * limit, nbHits)}
                />
              );
            }}
          </StatsWrapper>
        </div>
      </InstantSearch>
    </div>
  );
}

// ─── No results ───────────────────────────────────────────────────────────────

function NoResults() {
  return (
    <section className="section">
      <div className="container container--xl">
        <div className="grid grid--std">
          <div className="grid__col-lg-8">
            <div className="section__header">
              <h4 className="section__header-text">Upcoming Events by Month</h4>
            </div>
            <div className="upcoming-events__list">
              I'm sorry no events were found matching that description.
            </div>
          </div>
          <div className="grid__col-lg-4"></div>
        </div>
        <div className="line-break__hr"></div>
      </div>
    </section>
  );
}
